import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, crosshairCursor, highlightActiveLine, placeholder, Decoration, DecorationSet, gutter, GutterMarker } from '@codemirror/view';
import { EditorState, StateField, StateEffect, RangeSetBuilder, RangeSet } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { debounce } from '../utils/debounce';

/**
 * State effect to add error markers
 */
const addErrorMarker = StateEffect.define<{ line: number; column: number }>();

/**
 * State effect to clear error markers
 */
const clearErrorMarkers = StateEffect.define<null>();

/**
 * Error gutter marker
 */
class ErrorMarker extends GutterMarker {
    toDOM() {
        const marker = document.createElement('div');
        marker.style.color = '#ff0000';
        marker.style.cursor = 'pointer';
        marker.textContent = '●';
        marker.title = 'Parse error on this line';
        return marker;
    }
}

/**
 * State field for error line tracking
 */
const errorLineField = StateField.define<number | null>({
    create() {
        return null;
    },
    update(errorLine, tr) {
        for (let effect of tr.effects) {
            if (effect.is(addErrorMarker)) {
                return effect.value.line;
            } else if (effect.is(clearErrorMarkers)) {
                return null;
            }
        }
        return errorLine;
    },
});

/**
 * State field for error decorations
 */
const errorField = StateField.define<DecorationSet>({
    create() {
        return Decoration.none;
    },
    update(decorations, tr) {
        decorations = decorations.map(tr.changes);

        for (let effect of tr.effects) {
            if (effect.is(addErrorMarker)) {
                const { line, column } = effect.value;
                const docLine = tr.state.doc.line(line);
                const pos = docLine.from + column - 1;
                const lineEnd = docLine.to;

                // Ensure we have a valid range (at least 1 character)
                // If at end of line, mark the last character
                let from = Math.min(pos, lineEnd);
                let to = Math.min(pos + 1, lineEnd);

                // If the range would be empty (at end of line), mark the last character
                if (from >= to && docLine.length > 0) {
                    from = lineEnd - 1;
                    to = lineEnd;
                }

                // Only create decoration if we have a valid non-empty range
                if (from < to) {
                    const underline = Decoration.mark({
                        class: 'cm-error-underline',
                    }).range(from, to);

                    decorations = Decoration.set([underline]);
                } else {
                    decorations = Decoration.none;
                }
            } else if (effect.is(clearErrorMarkers)) {
                decorations = Decoration.none;
            }
        }

        return decorations;
    },
    provide: f => EditorView.decorations.from(f),
});

/**
 * Error gutter field
 */
const errorGutter = gutter({
    class: 'cm-error-gutter',
    markers: v => {
        const errorLine = v.state.field(errorLineField);
        if (errorLine === null) {
            return RangeSet.empty;
        }

        const builder = new RangeSetBuilder<GutterMarker>();
        const line = v.state.doc.line(errorLine);
        builder.add(line.from, line.from, new ErrorMarker());
        return builder.finish();
    },
    initialSpacer: () => new ErrorMarker(),
});

export interface EditorOptions {
    onChange: (value: string) => void;
    onDebugGridChange: (enabled: boolean) => void;
}

export class Editor {
    private editorView: EditorView;
    private debugGridCheckbox: HTMLInputElement;
    private debouncedOnChange: (value: string) => void;

    constructor(private options: EditorOptions) {
        const container = document.getElementById('mermaid-input');
        this.debugGridCheckbox = document.getElementById('debug-grid') as HTMLInputElement;

        if (!container || !this.debugGridCheckbox) {
            throw new Error('Required editor elements not found');
        }

        // Debounce the onChange callback
        this.debouncedOnChange = debounce(options.onChange, 300);

        // Create CodeMirror editor
        const placeholderText = `Enter your Mermaid flowchart syntax here...

Example:
flowchart TD
    A[Start] --> B{Is it?}
    B -->|Yes| C[OK]
    C --> D[Rethink]
    D --> B
    B ---->|No| E[End]`;

        const startState = EditorState.create({
            doc: '',
            extensions: [
                lineNumbers(),
                highlightActiveLineGutter(),
                highlightSpecialChars(),
                history(),
                foldGutter(),
                drawSelection(),
                dropCursor(),
                EditorState.allowMultipleSelections.of(true),
                bracketMatching(),
                rectangularSelection(),
                crosshairCursor(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                placeholder(placeholderText),
                keymap.of([
                    ...defaultKeymap,
                    ...searchKeymap,
                    ...historyKeymap,
                    ...foldKeymap,
                ]),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged) {
                        const content = update.state.doc.toString();
                        console.log('Editor content changed:', content.substring(0, 50) + '...');
                        this.debouncedOnChange(content);
                    }
                }),
                EditorView.theme({
                    '&': {
                        height: '100%',
                        fontSize: '14px',
                    },
                    '.cm-scroller': {
                        overflow: 'auto',
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                    },
                    '.cm-content': {
                        padding: '10px 0',
                    },
                    '.cm-line': {
                        padding: '0 10px',
                    },
                    '.cm-placeholder': {
                        color: '#999',
                    },
                    '.cm-error-underline': {
                        textDecoration: 'underline wavy red',
                        textDecorationSkipInk: 'none',
                    },
                    '.cm-error-gutter': {
                        width: '1.5em',
                    },
                }),
                errorLineField,
                errorField,
                errorGutter,
                EditorView.lineWrapping,
            ],
        });

        this.editorView = new EditorView({
            state: startState,
            parent: container,
        });

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        // Debug grid checkbox
        this.debugGridCheckbox.addEventListener('change', () => {
            this.options.onDebugGridChange(this.debugGridCheckbox.checked);
        });
    }

    getValue(): string {
        return this.editorView.state.doc.toString();
    }

    setValue(value: string): void {
        // Replace the entire document content
        this.editorView.dispatch({
            changes: {
                from: 0,
                to: this.editorView.state.doc.length,
                insert: value,
            },
        });

        // Manually trigger the onChange callback since setValue is called programmatically
        // and we want immediate feedback (not debounced)
        this.options.onChange(value);
    }

    getDebugGrid(): boolean {
        return this.debugGridCheckbox.checked;
    }

    clear(): void {
        this.setValue('');
    }

    /**
     * Mark an error at the specified line and column
     */
    markError(line: number, column: number): void {
        this.editorView.dispatch({
            effects: addErrorMarker.of({ line, column }),
        });
    }

    /**
     * Clear all error markers
     */
    clearErrors(): void {
        this.editorView.dispatch({
            effects: clearErrorMarkers.of(null),
        });
    }
}
